import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { AlertTriangle, ClipboardCheck, PackageX, Scale } from "lucide-react";

import OpenWorkQueueCards from "../../components/OpenWorkQueueCards";
import {
  Button,
  EmptyState,
  ErrorState,
  PageHeader,
  SkeletonRows,
  Surface,
  Table,
  TableContainer,
  TBody,
  Td,
  Th,
  THead,
  Tr,
} from "../../../components/ui";
import { inventoryAPI, logisticsAPI } from "../../services/businessErpService";
import { getDeliveryLabel, getPOLabel } from "../../../utils/documentLabels";

function WorkTable({ id, labelledBy, title, subtitle, rows, columns, emptyTitle, emptyDescription, href }) {
  return (
    <Surface
      as="section"
      id={id}
      role="tabpanel"
      aria-labelledby={labelledBy}
      toolbar={(
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-section-title text-foreground">{title}</h2>
            <p className="mt-1 text-body-sm text-secondary">{subtitle}</p>
          </div>
          <Button as={Link} to={href} size="sm" variant="secondary">Open workspace</Button>
        </div>
      )}
    >
      {!rows.length ? (
        <EmptyState title={emptyTitle} description={emptyDescription} />
      ) : (
        <TableContainer label={title}>
          <Table className="min-w-[760px]" caption={subtitle}>
            <THead>
              <Tr>
                {columns.map((column) => <Th key={column.key}>{column.label}</Th>)}
              </Tr>
            </THead>
            <TBody>
              {rows.map((row) => (
                <Tr key={`${title}-${row.id || row.variant_id || row.delivery_number}`}>
                  {columns.map((column) => (
                    <Td key={column.key}>{column.render ? column.render(row) : row[column.key]}</Td>
                  ))}
                </Tr>
              ))}
            </TBody>
          </Table>
        </TableContainer>
      )}
    </Surface>
  );
}

export default function InventoryOpenWork() {
  const [stockLevels, setStockLevels] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [levelsRes, deliveriesRes] = await Promise.all([
        inventoryAPI.listStockLevels({ limit: 250 }),
        logisticsAPI.list({ limit: 100 }),
      ]);
      setStockLevels(Array.isArray(levelsRes?.data) ? levelsRes.data : []);
      setDeliveries(Array.isArray(deliveriesRes?.data?.deliveries)
        ? deliveriesRes.data.deliveries
        : Array.isArray(deliveriesRes?.data) ? deliveriesRes.data : []);
    } catch {
      setStockLevels([]);
      setDeliveries([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const summary = useMemo(() => {
    const lowStock = stockLevels.filter((row) => Number(row.quantity ?? 0) > 0 && Number(row.quantity ?? 0) <= Number(row.low_stock_threshold ?? 5));
    const outOfStock = stockLevels.filter((row) => Number(row.quantity ?? 0) <= 0);
    const pendingReceipts = deliveries.filter((row) =>
      ["PENDING_RECEIPT", "PARTIALLY_RECEIVED", "RECEIVED_WITH_DISCREPANCY"].includes(String(row.receiving_status || "").toUpperCase()),
    );
    const discrepancies = pendingReceipts.filter((row) => String(row.receiving_status || "").toUpperCase() === "RECEIVED_WITH_DISCREPANCY");
    return { lowStock, outOfStock, pendingReceipts, discrepancies };
  }, [deliveries, stockLevels]);

  const lanes = [
    { id: "lowStock", title: "Low Stock", description: "Variants below their reorder threshold", actionLabel: "Review low stock", icon: AlertTriangle, tone: "warning", count: summary.lowStock.length },
    { id: "outOfStock", title: "Out of Stock", description: "Variants with zero on-hand quantity", actionLabel: "Review stockouts", icon: PackageX, tone: "danger", count: summary.outOfStock.length },
    { id: "pendingReceipts", title: "Pending Receipts", description: "Delivered shipments still waiting on business confirmation", actionLabel: "Review receipts", icon: ClipboardCheck, tone: "accent", count: summary.pendingReceipts.length },
    { id: "discrepancies", title: "Discrepancies", description: "Receipts with rejected or discrepant quantities", actionLabel: "Review discrepancies", icon: Scale, tone: "danger", count: summary.discrepancies.length },
  ];
  const requestedLane = searchParams.get("lane");
  const activeLane = lanes.some((item) => item.id === requestedLane)
    ? requestedLane
    : lanes.find((item) => item.count > 0)?.id ?? lanes[0].id;

  const commonPanelProps = {
    id: "inventory-work-panel",
    labelledBy: `inventory-queue-${activeLane}`,
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Inventory Open Work"
        description="Review low-stock, out-of-stock, and receipt-posting issues from a dedicated inventory action queue."
        actions={<Button as={Link} to="/business/inventory" size="sm">Open inventory workspace</Button>}
      />

      <OpenWorkQueueCards
        queues={lanes}
        active={activeLane}
        loading={loading}
        onChange={(nextLane) => setSearchParams({ lane: nextLane })}
        idPrefix="inventory-queue"
        panelId="inventory-work-panel"
        label="Inventory work queues"
      />

      {loading ? (
        <Surface id="inventory-work-panel" role="tabpanel" aria-label="Loading inventory work">
          <SkeletonRows rows={5} columns={5} label="Loading inventory work" />
        </Surface>
      ) : null}
      {!loading && loadError ? (
        <Surface id="inventory-work-panel" role="tabpanel" aria-label="Inventory work error">
          <ErrorState title="Could not load inventory work" description="Please try again." onRetry={load} />
        </Surface>
      ) : null}
      {!loading && !loadError && activeLane === "lowStock" ? (
        <WorkTable
          {...commonPanelProps}
          title="Low-stock Variants"
          subtitle="Variants below their reorder threshold"
          href="/business/inventory"
          rows={summary.lowStock.slice(0, 12)}
          emptyTitle="No low-stock variants"
          emptyDescription="All tracked variants are above their threshold."
          columns={[
            { key: "product_name", label: "Product" },
            { key: "variant_title", label: "Variant", render: (row) => row.variant_title || "Default" },
            { key: "sku", label: "SKU", render: (row) => row.sku || "—" },
            { key: "quantity", label: "On Hand" },
            { key: "low_stock_threshold", label: "Threshold" },
          ]}
        />
      ) : null}
      {!loading && !loadError && activeLane === "outOfStock" ? (
        <WorkTable
          {...commonPanelProps}
          title="Out-of-stock Variants"
          subtitle="Variants with zero on-hand quantity"
          href="/business/inventory"
          rows={summary.outOfStock.slice(0, 12)}
          emptyTitle="No stockouts"
          emptyDescription="No tracked variant is currently at zero stock."
          columns={[
            { key: "product_name", label: "Product" },
            { key: "variant_title", label: "Variant", render: (row) => row.variant_title || "Default" },
            { key: "sku", label: "SKU", render: (row) => row.sku || "—" },
            { key: "incoming", label: "Incoming", render: (row) => Number(row.incoming || 0) },
            { key: "reserved", label: "Reserved", render: (row) => Number(row.reserved || 0) },
          ]}
        />
      ) : null}
      {!loading && !loadError && activeLane === "pendingReceipts" ? (
        <WorkTable
          {...commonPanelProps}
          title="Pending Receipt Posting"
          subtitle="Delivered shipments still waiting on business confirmation"
          href="/business/inventory/receipts"
          rows={summary.pendingReceipts.slice(0, 12)}
          emptyTitle="No receipts pending"
          emptyDescription="All delivered shipments have already been posted or closed."
          columns={[
            { key: "delivery_number", label: "Delivery", render: (row) => getDeliveryLabel(row) },
            { key: "po_number", label: "PO", render: (row) => getPOLabel(row) },
            { key: "supplier_name", label: "Supplier" },
            { key: "product_name", label: "Product" },
            { key: "receiving_status", label: "Receiving Status" },
          ]}
        />
      ) : null}
      {!loading && !loadError && activeLane === "discrepancies" ? (
        <WorkTable
          {...commonPanelProps}
          title="Receipt Discrepancies"
          subtitle="Receipts with rejected or discrepant quantities"
          href="/business/inventory/receipts"
          rows={summary.discrepancies.slice(0, 12)}
          emptyTitle="No discrepancies"
          emptyDescription="There are no receipt discrepancies waiting on follow-through."
          columns={[
            { key: "delivery_number", label: "Delivery", render: (row) => getDeliveryLabel(row) },
            { key: "po_number", label: "PO", render: (row) => getPOLabel(row) },
            { key: "supplier_name", label: "Supplier" },
            { key: "product_name", label: "Product" },
            { key: "status", label: "Supplier Status" },
          ]}
        />
      ) : null}
    </div>
  );
}
